import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Chip,
  Avatar,
  CircularProgress,
  InputAdornment,
  Divider,
  List,
  ListItemButton,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import { useVoyagerStore } from '../../stores/useVoyagerStore';
import { getReports } from '../../apis';
import { getCurrentUser, timeAgo, stringToColor } from '../../utils';
import { clerkEnabled } from '../../auth/clerk';
import { SavedModel } from '../../types';

interface FetchSavedProps {
  open: boolean;
  onClose: () => void;
}

const FetchSaved: React.FC<FetchSavedProps> = ({ open, onClose }) => {
  const [reports, setReports] = useState<SavedModel[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState<string | null>(null); // null = "All"
  const [viewMode, setViewMode] = useState<'reports' | 'templates'>('reports');
  const [perspectiveFilter, setPerspectiveFilter] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<SavedModel | null>(null);

  const loadReport = useVoyagerStore((s) => s.loadReport);
  const storeDeleteReport = useVoyagerStore((s) => s.deleteReport);
  const currentUser = getCurrentUser();

  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setSearch('');
      setAuthorFilter(null);
      setViewMode('reports');
      setPerspectiveFilter('');
      setLoading(true);
      getReports()
        .then((data) => setReports(data))
        .catch(() => setReports([]))
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Derive unique authors
  const authors = useMemo(() => {
    const set = new Set<string>();
    reports.forEach((r) => {
      if (r.created_by) set.add(r.created_by);
    });
    return Array.from(set).sort();
  }, [reports]);

  // Derive unique perspectives from template reports (for perspective filter)
  const templatePerspectives = useMemo(() => {
    const set = new Set<string>();
    reports.forEach((r) => {
      if (r.isTemplate && r.perspective) set.add(r.perspective);
    });
    return Array.from(set).sort();
  }, [reports]);

  // Filter reports by viewMode, search, author, and perspective
  const filtered = useMemo(() => {
    // First split by view mode
    let result = viewMode === 'templates'
      ? reports.filter((r) => r.isTemplate === true)
      : reports.filter((r) => !r.isTemplate);

    // Author filter. With Clerk enabled the server already returns only the
    // signed-in user's reports, so "mine" is everything — the localStorage
    // display-name comparison would wrongly empty the list.
    if (authorFilter === '__mine__') {
      if (!clerkEnabled()) result = result.filter((r) => r.created_by === currentUser);
    } else if (authorFilter) {
      result = result.filter((r) => r.created_by === authorFilter);
    }

    // Perspective filter (templates mode only)
    if (viewMode === 'templates' && perspectiveFilter) {
      result = result.filter((r) => r.perspective === perspectiveFilter);
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q)) ||
          (r.perspective && r.perspective.toLowerCase().includes(q)) ||
          (r.templateTags && r.templateTags.some((t) => t.toLowerCase().includes(q)))
      );
    }

    return result;
  }, [reports, viewMode, authorFilter, perspectiveFilter, search, currentUser]);

  const handleLoad = (id: string) => {
    loadReport(id);
    onClose();
  };

  const getTimestamp = (report: SavedModel): string => {
    const ts = report.createdAt || report.created_at;
    return ts ? timeAgo(ts) : '';
  };

  const getAuthor = (report: SavedModel): string => {
    return report.created_by || 'anonymous';
  };

  const getInitial = (author: string): string => {
    if (!author || author === 'anonymous') return '?';
    return author.charAt(0).toUpperCase();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {viewMode === 'templates' ? 'Browse Templates' : 'Load Report'}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* View mode toggle */}
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', gap: 0.5 }}>
          <Chip
            label="Reports"
            size="small"
            variant={viewMode === 'reports' ? 'filled' : 'outlined'}
            color={viewMode === 'reports' ? 'primary' : 'default'}
            onClick={() => { setViewMode('reports'); setSelectedId(null); setPerspectiveFilter(''); }}
            sx={{ fontSize: 12, fontWeight: viewMode === 'reports' ? 600 : 400 }}
          />
          <Chip
            label="Templates"
            size="small"
            variant={viewMode === 'templates' ? 'filled' : 'outlined'}
            color={viewMode === 'templates' ? 'primary' : 'default'}
            onClick={() => { setViewMode('templates'); setSelectedId(null); setAuthorFilter(null); }}
            sx={{ fontSize: 12, fontWeight: viewMode === 'templates' ? 600 : 400 }}
          />
        </Box>

        {/* Search bar */}
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={viewMode === 'templates' ? 'Search templates...' : 'Search reports...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ '& input': { fontSize: 13 } }}
          />
        </Box>

        {/* Author filter chips (reports mode) */}
        {viewMode === 'reports' && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label="All Reports"
              size="small"
              variant={authorFilter === null ? 'filled' : 'outlined'}
              color={authorFilter === null ? 'primary' : 'default'}
              onClick={() => setAuthorFilter(null)}
              sx={{ fontSize: 12 }}
            />
            {currentUser && (
              <Chip
                label="My Reports"
                size="small"
                variant={authorFilter === '__mine__' ? 'filled' : 'outlined'}
                color={authorFilter === '__mine__' ? 'primary' : 'default'}
                onClick={() => setAuthorFilter('__mine__')}
                sx={{ fontSize: 12 }}
              />
            )}
            {authors
              .filter((a) => a !== currentUser)
              .map((author) => (
                <Chip
                  key={author}
                  label={author}
                  size="small"
                  variant={authorFilter === author ? 'filled' : 'outlined'}
                  color={authorFilter === author ? 'primary' : 'default'}
                  onClick={() => setAuthorFilter(author)}
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: stringToColor(author),
                        width: 20,
                        height: 20,
                        fontSize: 11,
                      }}
                    >
                      {getInitial(author)}
                    </Avatar>
                  }
                  sx={{ fontSize: 12 }}
                />
              ))}
          </Box>
        )}

        {/* Perspective filter (templates mode) */}
        {viewMode === 'templates' && templatePerspectives.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <FormControl size="small" fullWidth>
              <Select
                value={perspectiveFilter}
                onChange={(e) => setPerspectiveFilter(e.target.value)}
                displayEmpty
                sx={{ fontSize: 13 }}
              >
                <MenuItem value="">All Perspectives</MenuItem>
                {templatePerspectives.map((dm) => (
                  <MenuItem key={dm} value={dm}>{dm}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Divider />

        {/* Report list */}
        <Box sx={{ minHeight: 200, maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                color: 'text.secondary',
              }}
            >
              <FolderOpenIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">
                {viewMode === 'templates'
                  ? (filtered.length === 0 && !search && !perspectiveFilter
                      ? 'No templates created yet'
                      : 'No templates match your filters')
                  : (reports.length === 0
                      ? 'No reports saved yet'
                      : 'No reports match your filters')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((report, idx) => {
                const author = getAuthor(report);
                const timestamp = getTimestamp(report);
                return (
                  <React.Fragment key={report.id}>
                    {idx > 0 && <Divider />}
                    <ListItemButton
                      selected={selectedId === report.id}
                      onClick={() => setSelectedId(report.id)}
                      onDoubleClick={() => handleLoad(report.id)}
                      sx={{ px: 2, py: 1.5, alignItems: 'flex-start' }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: stringToColor(author),
                          width: 36,
                          height: 36,
                          fontSize: 16,
                          mr: 1.5,
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      >
                        {getInitial(author)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.25,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {report.name}
                          </Typography>
                          <Chip
                            label={report.perspective}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              '& .MuiChip-label': { px: 0.75 },
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                        {report.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: 12,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mb: 0.25,
                            }}
                          >
                            {report.description}
                          </Typography>
                        )}
                        {/* Template tags */}
                        {viewMode === 'templates' && report.templateTags && report.templateTags.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                            {report.templateTags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                          {author}{timestamp ? ` \u00B7 ${timestamp}` : ''}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(report);
                        }}
                        sx={{ ml: 1, mt: 0.25, flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={() => selectedId && handleLoad(selectedId)}
          variant="contained"
          disabled={!selectedId}
        >
          {viewMode === 'templates' ? 'Use Template' : 'Load Report'}
        </Button>
      </DialogActions>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!deleteTarget) return;
              const id = deleteTarget.id;
              await storeDeleteReport(id);
              setReports((prev) => prev.filter((r) => r.id !== id));
              if (selectedId === id) setSelectedId(null);
              setDeleteTarget(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FetchSaved;
